import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Key,
  Fingerprint,
  FileCheck,
  CheckCircle2,
  ArrowRight,
  Building2,
  CreditCard,
  Smartphone,
  BadgeCheck,
  ScanFace,
  ShieldCheck,
  X,
  Minus,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import BookDemoModal from '../components/BookDemoModal';
import ContactSalesModal from '../components/ContactSalesModal';
import { useLanguage } from '../context/LanguageContext';

// Philippine-specific features
const coreFeatures = [
  {
    icon: FileCheck,
    title: 'Advanced OCR Extraction',
    titleFil: 'Advanced OCR Extraction',
    description: 'Automated data extraction from 11+ Philippine IDs with 99.9% accuracy - no more manual typing.',
    descriptionFil: 'Awtomatikong pagkuha ng datos mula sa 11+ na Philippine IDs na may 99.9% accuracy.'
  },
  {
    icon: 'logo',
    title: 'QR & Barcode Validation',
    titleFil: 'QR & Barcode Validation',
    description: 'Multi-format barcode scanning for PhilID (QR), Driver\'s License (PDF417), and Postal ID - protected against tampered physical IDs.',
    descriptionFil: 'Multi-format barcode scanning para sa PhilID (QR), Driver\'s License (PDF417), at Postal ID - protektado laban sa tampered na physical IDs.'
  },
  {
    icon: Fingerprint,
    title: 'Biometric Verification',
    titleFil: 'Biometric Verification',
    description: 'Face matching with liveness detection and anti-spoofing to prevent identity theft.',
    descriptionFil: 'Face matching na may liveness detection at anti-spoofing.'
  },
  {
    icon: Key,
    title: 'Cryptographic Audit Trail',
    titleFil: 'Cryptographic Audit Trail',
    description: 'Secure SHA-256 hashing for immutable verification records and enterprise compliance.',
    descriptionFil: 'Secure na SHA-256 hashing para sa immutable verification records.'
  }
];

// Philippine IDs supported
const supportedIds = [
  { name: 'PhilID', full: 'Philippine National ID', agency: 'PSA', status: 'QR + Template Match' },
  { name: 'Passport', full: 'Philippine Passport', agency: 'DFA', status: 'MRZ + Biometric' },
  { name: "Driver's License", full: 'LTO License', agency: 'LTO', status: 'PDF417 Scan' },
  { name: 'UMID', full: 'Unified Multi-Purpose ID', agency: 'SSS/GSIS', status: 'OCR Assessment' },
  { name: 'Postal ID', full: 'Philippine Postal ID', agency: 'PHLPost', status: 'QR Scan' },
  { name: 'PRC ID', full: 'Professional License', agency: 'PRC', status: 'OCR Assessment' },
  { name: 'TIN Card', full: 'Tax Identification Number', agency: 'BIR', status: 'OCR Assessment' },
  { name: 'PhilHealth', full: 'PhilHealth ID', agency: 'PhilHealth', status: 'OCR Assessment' },
  { name: "Voter's ID", full: 'COMELEC ID', agency: 'COMELEC', status: 'OCR Assessment' },
];

// Payment methods
const paymentMethods = [
  { name: 'Bank QR / InstaPay', users: 'All PH banks' },
  { name: 'GCash', users: 'Coming soon' },
  { name: 'Maya', users: 'Coming soon' },
];

// Use cases for Philippine market
const useCases = [
  {
    icon: Building2,
    title: 'Digital Banks',
    titleFil: 'Digital Banks',
    description: 'Engineered for BSP eKYC compliance – pilot-ready for digital banks and fintechs',
    clients: ['Pilot Ready', 'BSP Sandbox']
  },
  {
    icon: CreditCard,
    title: 'Lending Apps',
    titleFil: 'Lending Apps',
    description: 'AMLA-compliant borrower verification for online lending',
    clients: ['Tala', 'Cashalo', 'JuanHand']
  },
  {
    icon: Smartphone,
    title: 'E-Commerce',
    titleFil: 'E-Commerce',
    description: 'Seller verification to reduce COD fraud',
    clients: ['Lazada', 'Shopee', 'Zalora']
  },
  {
    icon: BadgeCheck,
    title: 'HR & Recruitment',
    titleFil: 'HR & Recruitment',
    description: 'Employee background verification',
    clients: ['JobStreet', 'Kalibrr', 'OnlineJobs.ph']
  }
];

// Credit bundle pricing
const creditBundles = [
  {
    name: 'Personal',
    price: '100',
    basicCredits: 4,
    enhancedCredits: 2,
    bonus: '0%',
    effectivePrice: '₱25',
    forIndividuals: true,
  },
  {
    name: 'Starter',
    price: '500',
    basicCredits: 20,
    enhancedCredits: 11,
    bonus: '0%',
    effectivePrice: '₱25',
  },
  {
    name: 'Business',
    price: '2,500',
    basicCredits: 120,
    enhancedCredits: 66,
    bonus: '+20%',
    effectivePrice: '₱21',
    featured: true,
  },
  {
    name: 'Enterprise',
    price: '5,000',
    basicCredits: 260,
    enhancedCredits: 143,
    bonus: '+30%',
    effectivePrice: '₱19',
  },
];

// Compliance badges
const complianceBadges = [
  { name: 'BSP eKYC Compliance-Ready', description: 'Bangko Sentral ng Pilipinas' },
  { name: 'Registration in Progress', description: 'National Privacy Commission' },
  { name: 'AMLA Compliant', description: 'Anti-Money Laundering Act' },
  { name: 'Security-First Architecture', description: '24-hr Purge • Encrypted • Audit Trails' },
];

// Comparison table rows
const comparisonRows = [
  {
    feature: 'Price per verification',
    featureFil: 'Presyo bawat verification',
    agos: '₱19–25 (bundles)',
    vendors: '₱110 – ₱330',
    agosHighlight: true,
  },
  {
    feature: 'PhilSys QR Validation',
    featureFil: 'PhilSys QR Validation',
    sub: 'Format check + data extraction',
    subFil: 'Format check + data extraction',
    agos: 'QR Format Validation',
    agosSubtext: 'Data extraction included',
    vendors: null,
  },
  {
    feature: 'Local Payment (Bank QR / InstaPay)',
    featureFil: 'Local Payment (Bank QR / InstaPay)',
    agos: true,
    vendors: false,
    vendorNote: 'Credit card only',
  },
  {
    feature: 'BSP eKYC Ready',
    featureFil: 'BSP eKYC Ready',
    agos: true,
    vendors: 'partial',
    vendorNote: 'Manual compliance',
  },
  {
    feature: 'Philippine ID Templates',
    featureFil: 'Philippine ID Templates',
    agos: '11 templates',
    vendors: '3–5 templates',
    agosHighlight: true,
  },
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
};

// ── Verification Flow Hero Card ──────────────────────────────────────────────
// Replaces the barcode video — shows an animated live ID verification sequence
const verificationSteps = [
  { label: 'Scanning PhilID', sublabel: 'QR code detected', icon: FileCheck, done: true },
  { label: 'OCR Extraction', sublabel: '11 fields captured', icon: Key, done: true },
  { label: 'Face Match', sublabel: 'Liveness verified', icon: ScanFace, done: true },
  { label: 'BSP eKYC Check', sublabel: 'Compliance passed', icon: ShieldCheck, done: false },
];

function HeroVerificationCard() {
  return (
    <div className="relative w-full max-w-2xl mx-auto mt-12">
      {/* Outer glow */}
      <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-teal-500/20 via-transparent to-cyan-500/10 blur-xl pointer-events-none" />

      <div className="relative rounded-2xl border border-teal-700/30 bg-slate-900/80 backdrop-blur-sm overflow-hidden shadow-2xl">
        {/* Top bar */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-800 bg-slate-900/50">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/70" />
          <span className="ml-3 text-xs text-slate-500 font-mono">agos — live verification</span>
          <div className="ml-auto flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse" />
            <span className="text-[10px] text-teal-400 font-medium">LIVE</span>
          </div>
        </div>

        <div className="grid grid-cols-5 divide-x divide-slate-800">
          {/* Left: mock ID card */}
          <div className="col-span-2 p-4 flex flex-col gap-3">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Document</p>
            {/* ID card mockup */}
            <motion.div
              animate={{ boxShadow: ['0 0 0 0 rgba(20,184,166,0)', '0 0 0 6px rgba(20,184,166,0.15)', '0 0 0 0 rgba(20,184,166,0)'] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="relative rounded-lg bg-gradient-to-br from-slate-700 to-slate-800 border border-slate-600/50 p-3 aspect-[1.586/1] flex flex-col justify-between overflow-hidden"
            >
              {/* Scan line animation */}
              <motion.div
                animate={{ top: ['10%', '90%', '10%'] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-teal-400/70 to-transparent"
                style={{ position: 'absolute' }}
              />
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <div className="h-1.5 w-16 rounded bg-slate-600" />
                  <div className="h-1 w-10 rounded bg-slate-700" />
                </div>
                <div className="w-7 h-7 rounded-full bg-slate-600/80 border border-slate-500/50" />
              </div>
              <div className="space-y-1">
                <div className="h-1 w-12 rounded bg-teal-500/40" />
                <div className="h-1 w-20 rounded bg-slate-600" />
                <div className="h-1 w-14 rounded bg-slate-700" />
              </div>
              {/* Teal corner accent */}
              <div className="absolute top-0 right-0 w-8 h-8 bg-teal-500/20 rounded-bl-xl" />
            </motion.div>

            {/* Confidence score */}
            <div className="mt-1">
              <div className="flex justify-between text-[10px] text-slate-500 mb-1">
                <span>Confidence</span>
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.5 }}
                  className="text-teal-400 font-bold"
                >
                  97.4%
                </motion.span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: '97.4%' }}
                  transition={{ delay: 1.2, duration: 1.2, ease: 'easeOut' }}
                  className="h-full rounded-full bg-gradient-to-r from-teal-600 to-teal-400"
                />
              </div>
            </div>
          </div>

          {/* Right: steps */}
          <div className="col-span-3 p-4 flex flex-col gap-2">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1">Verification Pipeline</p>
            {verificationSteps.map((step, i) => (
              <motion.div
                key={step.label}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + i * 0.35, duration: 0.4 }}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 border transition-colors ${
                  step.done
                    ? 'bg-teal-500/5 border-teal-600/20'
                    : 'bg-slate-800/40 border-slate-700/30'
                }`}
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                  step.done ? 'bg-teal-500/20' : 'bg-slate-700'
                }`}>
                  {step.done
                    ? <CheckCircle2 className="w-3.5 h-3.5 text-teal-400" />
                    : (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                        className="w-3 h-3 rounded-full border-2 border-teal-500/40 border-t-teal-400"
                      />
                    )
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-semibold truncate ${step.done ? 'text-slate-200' : 'text-slate-400'}`}>
                    {step.label}
                  </p>
                  <p className="text-[10px] text-slate-500 truncate">{step.sublabel}</p>
                </div>
                {step.done && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.6 + i * 0.35, type: 'spring' }}
                    className="text-[9px] font-bold text-teal-400 bg-teal-500/10 px-1.5 py-0.5 rounded uppercase tracking-wide shrink-0"
                  >
                    Pass
                  </motion.span>
                )}
              </motion.div>
            ))}

            {/* Final result */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 2.2, duration: 0.4 }}
              className="mt-1 flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2"
            >
              <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
              <div>
                <p className="text-xs font-bold text-emerald-400">Identity Verified</p>
                <p className="text-[10px] text-slate-500">BSP eKYC compliant · 2.3s total</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Floating stat pills */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.8 }}
        className="absolute -left-4 top-1/3 hidden lg:flex items-center gap-2 bg-slate-800/90 border border-slate-700 px-3 py-2 rounded-xl shadow-lg"
      >
        <div className="w-6 h-6 rounded-full bg-teal-500/20 flex items-center justify-center">
          <Fingerprint className="w-3.5 h-3.5 text-teal-400" />
        </div>
        <div>
          <p className="text-white text-xs font-bold leading-none">Liveness</p>
          <p className="text-teal-400 text-[10px] font-semibold">Anti-spoof active</p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2 }}
        className="absolute -right-4 top-1/2 hidden lg:flex items-center gap-2 bg-slate-800/90 border border-slate-700 px-3 py-2 rounded-xl shadow-lg"
      >
        <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
        </div>
        <div>
          <p className="text-white text-xs font-bold leading-none">11 ID Types</p>
          <p className="text-emerald-400 text-[10px] font-semibold">PH Government IDs</p>
        </div>
      </motion.div>
    </div>
  );
}

// ── Comparison Table ─────────────────────────────────────────────────────────
function ComparisonTable({ lang }: { lang: 'en' | 'fil' }) {
  return (
    <div className="rounded-2xl border border-slate-700/50 overflow-hidden bg-slate-900/50 backdrop-blur-sm">
      {/* Header */}
      <div className="grid grid-cols-3 border-b border-slate-700/50">
        <div className="py-4 px-5 text-sm text-slate-500 font-medium">
          {lang === 'en' ? 'Feature' : 'Feature'}
        </div>
        <div className="py-4 px-5 text-center border-x border-slate-700/50 bg-teal-500/5">
          <span className="text-sm font-bold text-teal-400">Agos Identity</span>
          <div className="text-[10px] text-teal-500/70 font-medium mt-0.5">Local · Affordable</div>
        </div>
        <div className="py-4 px-5 text-center">
          <span className="text-sm font-medium text-slate-500">
            {lang === 'en' ? 'International Vendors' : 'International Vendors'}
          </span>
          <div className="text-[10px] text-slate-600 mt-0.5">Jumio · Onfido · Veriff</div>
        </div>
      </div>

      {/* Rows */}
      {comparisonRows.map((row, i) => (
        <div
          key={i}
          className={`grid grid-cols-3 border-b border-slate-800/60 last:border-0 transition-colors hover:bg-slate-800/20 ${
            i % 2 === 0 ? '' : 'bg-slate-900/30'
          }`}
        >
          {/* Feature name */}
          <div className="py-4 px-5 flex flex-col justify-center">
            <span className="text-sm text-slate-300 font-medium">
              {lang === 'en' ? row.feature : row.featureFil}
            </span>
            {row.sub && (
              <span className="text-[10px] text-slate-600 mt-0.5">
                {lang === 'en' ? row.sub : row.subFil}
              </span>
            )}
          </div>

          {/* Agos value */}
          <div className="py-4 px-5 border-x border-slate-700/30 bg-teal-500/5 flex flex-col items-center justify-center text-center">
            {row.agos === true ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            ) : (
              <div>
                <span className={`text-sm font-semibold ${row.agosHighlight ? 'text-teal-400' : 'text-emerald-400'}`}>
                  {row.agos}
                </span>
                {row.agosSubtext && (
                  <p className="text-[10px] text-slate-500 mt-0.5">{row.agosSubtext}</p>
                )}
              </div>
            )}
          </div>

          {/* Vendor value */}
          <div className="py-4 px-5 flex flex-col items-center justify-center text-center">
            {row.vendors === true ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            ) : row.vendors === false ? (
              <div className="flex flex-col items-center gap-0.5">
                <X className="w-4 h-4 text-red-400/70" />
                {row.vendorNote && <span className="text-[10px] text-slate-600">{row.vendorNote}</span>}
              </div>
            ) : row.vendors === 'partial' ? (
              <div className="flex flex-col items-center gap-0.5">
                <Minus className="w-4 h-4 text-yellow-500/70" />
                {row.vendorNote && <span className="text-[10px] text-slate-600">{row.vendorNote}</span>}
              </div>
            ) : row.vendors === null ? (
              <div className="flex flex-col items-center gap-0.5">
                <X className="w-4 h-4 text-red-400/70" />
                <span className="text-[10px] text-slate-600">
                  {lang === 'en' ? 'Not available' : 'Hindi available'}
                </span>
              </div>
            ) : (
              <span className="text-sm text-slate-500">{row.vendors}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────
export default function LandingPage() {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const { lang: language } = useLanguage();

  const getText = (en: string, fil: string) => language === 'en' ? en : fil;

  return (
    <div className="relative w-full overflow-x-hidden bg-slate-950">
      {/* Background */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-teal-950/20 to-slate-900" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(20,184,166,0.12),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(6,182,212,0.08),transparent_55%)]" />
      </div>

      {/* ── Hero ── */}
      <section className="relative z-10 min-h-screen flex flex-col items-center justify-start px-4 pt-24 pb-16">
        <div className="max-w-4xl mx-auto w-full flex flex-col items-center text-center mt-8 lg:mt-12">

          {/* NPC Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-4 py-2 rounded-full mb-8 backdrop-blur-sm"
          >
            <CheckCircle2 className="w-4 h-4" />
            <span className="text-sm font-medium">NPC Registration in Progress · DPA Ready</span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight tracking-tight"
          >
            <span className="text-white">
              {getText('Affordable, secure ID verification', 'Abot-kayang, secure na ID verification')}
            </span>
            <br />
            <span className="bg-gradient-to-r from-teal-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent block mt-1">
              {getText('for Philippine fintech & platforms', 'para sa Philippine fintech & platforms')}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="text-lg md:text-xl text-slate-400 mb-6 max-w-3xl mx-auto leading-relaxed"
          >
            {getText(
              'Agos enables remote customer onboarding using Philippine IDs, biometrics, and local data handling — designed for startups and growing platforms that need reliable verification without enterprise vendor pricing.',
              'Nagbibigay-daan ang Agos para sa remote customer onboarding gamit ang Philippine IDs, biometrics, at local data handling — idinisenyo para sa mga startup at lumalagong platform.'
            )}
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-sm text-teal-400/80 mb-8 font-medium tracking-wide uppercase"
          >
            {getText(
              'Built with Philippine regulatory frameworks in mind — BSP eKYC, AMLA, and NPC data privacy.',
              'Binuo na isinasaalang-alang ang BSP eKYC, AMLA guidelines at NPC data privacy principles.'
            )}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-10"
          >
            <motion.button
              type="button"
              whileHover={{ scale: 1.03, boxShadow: '0 0 30px rgba(20,184,166,0.3)' }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsDemoModalOpen(true)}
              className="group px-8 py-4 bg-gradient-to-r from-teal-600 to-teal-500 text-white rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-teal-900/30"
            >
              {getText('Schedule Demo', 'Mag-schedule ng Demo')}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>
            <motion.button
              type="button"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsContactModalOpen(true)}
              className="px-8 py-4 bg-slate-800/50 border border-slate-700 text-slate-300 rounded-xl font-semibold hover:bg-slate-800 hover:border-slate-600 transition-all duration-300 backdrop-blur-sm"
            >
              {getText('Contact Sales', 'Makipag-ugnay sa Sales')}
            </motion.button>
          </motion.div>

          {/* Pricing preview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="flex flex-wrap items-center justify-center gap-3 mb-4"
          >
            <span className="text-slate-500 text-sm">{getText('Credit bundles from', 'Credit bundles mula')}</span>
            <span className="text-white font-bold text-sm">₱100 (₱25/verification)</span>
            <span className="text-slate-600 text-sm">·</span>
            <span className="text-slate-400 text-sm">{getText('Pay via Bank QR / InstaPay', 'Magbayad via Bank QR / InstaPay')}</span>
            <span className="text-slate-600 text-sm">·</span>
            <span className="text-emerald-400 text-sm font-medium">{getText('vs ₱110–330 from international vendors', 'vs ₱110–330 mula sa international vendors')}</span>
          </motion.div>
        </div>

        {/* Hero Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="w-full max-w-5xl mx-auto px-4 sm:px-8 lg:px-0"
        >
          <HeroVerificationCard />
        </motion.div>
      </section>

      {/* ── Supported IDs + Features wrapper ── */}
      <div className="relative z-10 bg-gradient-to-br from-slate-900 via-teal-950/20 to-slate-900 bg-[length:200%_200%] animate-gradient">

        {/* Supported IDs */}
        <section className="relative z-10 py-16 px-4 border-t border-slate-800/50">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}
              variants={fadeInUp} className="text-center mb-10"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {getText('Supported Government IDs', 'Mga Suportadong Government IDs')}
              </h2>
              <p className="text-slate-400">
                {getText('All major Philippine identification documents', 'Lahat ng major na Philippine identification documents')}
              </p>
            </motion.div>

            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={staggerContainer}
              className="flex flex-wrap justify-center gap-3"
            >
              {supportedIds.map((id) => (
                <motion.div
                  key={id.name}
                  variants={scaleIn}
                  whileHover={{
                    scale: 1.05,
                    borderColor: 'rgba(20,184,166,0.5)',
                    backgroundColor: 'rgba(15,23,42,0.95)',
                  }}
                  className="group bg-slate-800/80 border border-slate-700 rounded-xl px-6 py-5 backdrop-blur-md cursor-default transition-all duration-300 min-w-[200px] shadow-lg hover:shadow-teal-500/10"
                >
                  <div className="flex flex-col gap-3">
                    <div className="flex justify-between items-start gap-4">
                      <span className="text-white font-extrabold text-lg tracking-tight">{id.name}</span>
                      <span className="shrink-0 text-[10px] text-teal-300 font-bold uppercase tracking-wider bg-teal-900/50 border border-teal-700/30 px-2 py-1 rounded-md">
                        {id.status}
                      </span>
                    </div>
                    <div className="text-slate-300 text-xs font-semibold uppercase tracking-wider flex items-center gap-2">
                      <div className="w-1 h-1 rounded-full bg-teal-400" />
                      {id.agency} Issued
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Core Features */}
        <section className="relative z-10 py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}
              variants={fadeInUp} className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {getText('Enterprise-Grade Verification', 'Enterprise-Grade na Verification')}
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                {getText(
                  'Engineered for BSP eKYC Compliance. Built for NPC-ready architecture with localized data residency.',
                  'Dinisenyo para sa BSP eKYC Compliance. Ginawa para sa NPC-ready architecture na may localized data residency.'
                )}
              </p>
            </motion.div>

            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {coreFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  whileHover={{ y: -5, borderColor: 'rgba(20,184,166,0.3)' }}
                  className="group bg-slate-800/20 border border-slate-800 rounded-2xl p-6 backdrop-blur-sm transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-teal-500/20 transition-colors">
                    {feature.icon === 'logo' ? (
                      <img src="/pwa-192x192.png" alt="Agos Identity Logo" className="w-6 h-6 object-contain" />
                    ) : (
                      <feature.icon className="w-6 h-6 text-teal-400" />
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {getText(feature.title, feature.titleFil)}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {getText(feature.description, feature.descriptionFil)}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      </div>

      {/* ── Use Cases ── */}
      <section className="relative z-10 py-20 px-4 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp} className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {getText('Built for Philippine Industries', 'Ginawa para sa Philippine Industries')}
            </h2>
            <p className="text-slate-400">
              {getText('Engineered for leading digital banks and fintechs', 'Dinisenyo para sa mga nangungunang digital bank at fintech')}
            </p>
          </motion.div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -5, borderColor: 'rgba(20,184,166,0.25)' }}
                className="bg-gradient-to-b from-slate-800/40 to-slate-800/20 border border-slate-700/50 rounded-2xl p-6 backdrop-blur-sm transition-all duration-300"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500/20 to-cyan-500/10 rounded-lg flex items-center justify-center mb-4">
                  <useCase.icon className="w-5 h-5 text-teal-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {getText(useCase.title, useCase.titleFil)}
                </h3>
                <p className="text-slate-400 text-sm mb-4">{useCase.description}</p>
                <div className="flex flex-wrap gap-1">
                  {useCase.clients.map((client, i) => (
                    <span key={i} className="text-xs text-slate-500 bg-slate-800/50 px-2 py-1 rounded">
                      {client}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section className="relative z-10 py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp} className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {getText('Credit Bundle Pricing', 'Credit Bundle Pricing')}
            </h2>
            <p className="text-slate-400">
              {getText('Buy verification credits in bundles. Save more with larger bundles.', 'Bumili ng verification credits sa bundles. Mas makatipid sa mas malalaking bundle.')}
            </p>
          </motion.div>

          {/* Bundle cards */}
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10"
          >
            {creditBundles.map((bundle) => (
              <motion.div
                key={bundle.name}
                variants={scaleIn}
                whileHover={{ scale: 1.02 }}
                className={`bg-slate-800/30 border rounded-xl p-5 backdrop-blur-sm transition-all duration-300 ${
                  bundle.featured
                    ? 'border-teal-500/50 ring-1 ring-teal-500/20 shadow-lg shadow-teal-900/20'
                    : 'border-slate-700/50'
                }`}
              >
                {bundle.featured && (
                  <div className="text-xs text-teal-400 font-bold mb-2 text-center uppercase tracking-wider">
                    Popular
                  </div>
                )}
                <div className="text-center mb-3">
                  <div className="text-white font-semibold text-lg">{bundle.name}</div>
                  {bundle.bonus !== '0%' && (
                    <div className="text-emerald-400 text-xs font-medium">{bundle.bonus} bonus credits</div>
                  )}
                </div>
                <div className="text-center mb-4">
                  <span className="text-lg text-slate-400">₱</span>
                  <span className="text-3xl font-bold text-white">{bundle.price}</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-slate-400">
                    <span>{getText('Basic credits', 'Basic credits')}</span>
                    <span className="text-white font-medium">{bundle.basicCredits}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>{getText('Enhanced credits', 'Enhanced credits')}</span>
                    <span className="text-white font-medium">{bundle.enhancedCredits}</span>
                  </div>
                  <div className="border-t border-slate-700/50 pt-2 mt-2">
                    <div className="flex justify-between text-slate-400">
                      <span>{getText('Effective price', 'Effective price')}</span>
                      <span className="text-emerald-400 font-medium">{bundle.effectivePrice}/ea</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Payment methods */}
          <motion.div
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="flex flex-wrap justify-center items-center gap-4 mb-10"
          >
            <span className="text-slate-500 text-sm">{getText('Pay with:', 'Magbayad gamit ang:')}</span>
            {paymentMethods.map((method) => (
              <div key={method.name} className="flex items-center gap-1 text-slate-400 text-sm">
                <span>{method.name}</span>
                {method.users && <span className="text-slate-600 text-xs">({method.users})</span>}
              </div>
            ))}
          </motion.div>

          {/* Comparison Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mb-8"
          >
            <ComparisonTable lang={language} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-center"
          >
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 font-medium transition-colors"
            >
              {getText('View Full Pricing', 'Tingnan ang Buong Pricing')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── Compliance ── */}
      <section className="relative z-10 py-20 px-4 bg-slate-900/50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp} className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {getText('Compliance-First', 'Compliance-First')}
            </h2>
            <p className="text-slate-400">
              {getText('Engineered for Philippine regulatory standards', 'Dinisenyo para sa mga Philippine regulatory standards')}
            </p>
          </motion.div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            {complianceBadges.map((badge) => (
              <motion.div
                key={badge.name}
                variants={fadeInUp}
                whileHover={{ borderColor: 'rgba(20,184,166,0.3)' }}
                className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4 text-center backdrop-blur-sm transition-all duration-300"
              >
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <div className="text-white font-medium text-sm">{badge.name}</div>
                <div className="text-slate-500 text-xs">{badge.description}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="relative z-10 py-24 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={scaleIn}
            className="relative overflow-hidden bg-gradient-to-b from-slate-800/50 to-slate-900/50 border border-teal-700/20 rounded-3xl p-8 md:p-12 text-center backdrop-blur-sm"
          >
            {/* Subtle glow */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(20,184,166,0.08),transparent_70%)] pointer-events-none" />

            <h2 className="relative text-3xl md:text-4xl font-bold text-white mb-4">
              {getText('Ready to Start?', 'Handa Ka Na Ba?')}
            </h2>
            <p className="relative text-slate-400 mb-8 max-w-lg mx-auto">
              {getText(
                'Join Philippine banks and fintechs using Agos Identity for secure, compliant identity verification.',
                'Sumali sa mga Philippine banks at fintechs na gumagamit ng Agos Identity para sa secure at compliant na identity verification.'
              )}
            </p>
            <div className="relative flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                type="button"
                whileHover={{ scale: 1.03, boxShadow: '0 0 24px rgba(20,184,166,0.25)' }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setIsDemoModalOpen(true)}
                className="px-8 py-4 bg-gradient-to-r from-teal-600 to-teal-500 text-white rounded-xl font-semibold transition-all"
              >
                {getText('Schedule Demo', 'Mag-schedule ng Demo')}
              </motion.button>
              <Link
                to="/register"
                className="px-8 py-4 bg-slate-800 border border-slate-700 text-slate-300 rounded-xl font-semibold hover:bg-slate-700 transition-colors text-center"
              >
                {getText('Create Free Account', 'Gumawa ng Libreng Account')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Modals */}
      <BookDemoModal isOpen={isDemoModalOpen} onClose={() => setIsDemoModalOpen(false)} />
      <ContactSalesModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
    </div>
  );
}
